import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";

/**
 * Number input with a small mi / km toggle. Value is ALWAYS stored (and
 * emitted through onChange) in MILES to match the existing DB convention.
 * When the user flips to km, the visible number is converted on the fly.
 *
 * Props:
 *   value            — number in miles (or "" for empty)
 *   onChange(mi)     — receives the number in miles, or "" when cleared
 *   defaultUnit      — "mi" | "km" (initial display unit, default "mi")
 *   ...rest          — forwarded to <Input>
 */
const MI_PER_KM = 0.621371;

export default function MileageInput({ value, onChange, defaultUnit = "mi", ...rest }) {
  const [unit, setUnit] = useState(defaultUnit === "km" ? "km" : "mi");
  const [text, setText] = useState("");

  // Sync the visible text whenever the external miles value or unit changes.
  useEffect(() => {
    if (value === "" || value == null || Number.isNaN(Number(value))) {
      setText("");
      return;
    }
    const mi = Number(value);
    const shown = unit === "km" ? mi / MI_PER_KM : mi;
    // Trim to at most 1 decimal to avoid noisy conversions.
    setText(String(Math.round(shown * 10) / 10));
  }, [value, unit]);

  const emit = (t) => {
    setText(t);
    if (t === "" || t == null) {
      onChange("");
      return;
    }
    const n = Number(t);
    if (Number.isNaN(n)) return;
    const mi = unit === "km" ? n * MI_PER_KM : n;
    onChange(Math.round(mi));
  };

  return (
    <div className="flex items-stretch rounded-sm border border-slate-300 overflow-hidden focus-within:border-blue-500">
      <Input
        type="number"
        value={text}
        onChange={(e) => emit(e.target.value)}
        className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 tabular-nums"
        {...rest}
      />
      <div className="flex border-l border-slate-300 bg-slate-50">
        {["mi", "km"].map((u) => (
          <button
            key={u}
            type="button"
            onClick={() => setUnit(u)}
            className={`px-2 text-[10px] font-mono uppercase tracking-widest border-r border-slate-200 last:border-r-0 ${
              unit === u ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"
            }`}
            data-testid={`mileage-unit-${u}`}
          >
            {u}
          </button>
        ))}
      </div>
    </div>
  );
}
